import { useSelector } from "react-redux";
import { useContext, useMemo, createRef, useState, useCallback } from "react";
import ReactHtmlParser from "react-html-parser";
import isEmpty from "lodash/isEmpty";
import Link from "next/link";

import BreadcrumbContainer from "@stadium/ui/src/components/breadcrumb-container";
import Loaders from "@stadium/ui/src/components/loaders/loaders";
import { InternalButton } from "@components/shared/buttons";
import { BackIconButton } from "@sharedComponents/buttons";
import ShippingBanner from "@sharedComponents/banner/shipping";

import Multimedia from "@sharedComponents/multimedia";

import ProductListingBanner from "@sharedComponents/banner";
import ProductListingProvider from "@components/store/products/listing/listing-provider";
import ProductListing from "@components/store/products/listing/listing";
import ListingScrollReset from "@components/store/products/listing-scroll-reset";
import DummyStoreMerchBanner from "@components/store/products/listing/dummy-store-merch-banner";
import Sidebar from "@components/store/sidebar";
import FilterWrapper from "@stadium/shared/src/components/filter";
import { SwagCatalogBanner } from "@components/preview-catalog/banners";
import MobileMenuCategories from "@sharedComponents/responsive-menu-categories/mobile";
import TabMenuParentCategories from "@sharedComponents/responsive-menu-categories/tablet";

import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";
import { useConfig } from "@stadium/hooks/lib/use-config";
import { getStadiumCatalogNumber } from "@selectors/config";
import { getSelectedMenuItem, getParentCategoryFromCategory } from "@helpers/menu";
import { useShopAnalyticsContext } from "@hooks/analytics/use-shop-analytics-context";

import { STICKY_HEADER_REF } from "@stadium/ui/src/components/header";

import {
  getIsFiltersOnLeft,
  getIsLockerShop,
  getStoreNumber,
  getIsSinglePageShop,
  getShowBannerOnProductListing,
} from "@store/selectors/store";
import { getMenu, getMenuCodeMapping } from "@store/selectors/menu";

import { useStoreContext } from "@components/store/provider";
import { useCreateShopContext } from "@contexts/create-shop-context";
import useShowNewHeader from "@hooks/use-show-new-header";
import { useCreateSwagContext } from "@contexts/create-swag-context";

import { DEFAULT_MENU_ITEM_CODE } from "@constants/menu";
import {
  getMenuItemRoute,
  getStoreHomeRoute,
  ALL_PRODUCTS,
  getStoreBasePath,
  ALL_SUBCATEGORY_CODE,
} from "@constants/routes";
import { ANALYTICS_TARGET_FILTER } from "@constants/analytics/filter";

import styles from "./main.module.scss";

export const LISTING_SECTION_REF = createRef();

const StoreProductsListingContainer = ({
  category: categoryCode,
  slug,
  subCategory: subCategoryCode,
}) => {
  const storeNumber = useSelector(getStoreNumber);
  const menu = useSelector(getMenu);
  const menuCodeMapping = useSelector(getMenuCodeMapping);
  const showBannerOnProductListing = useSelector(getShowBannerOnProductListing);

  const { isMobileDevice, isDesktopDevice } = useContext(CurrentDeviceContext);

  const { isNewShopCreationFlow } = useCreateShopContext();

  const [showBrowseCatalogBanner, setShowBrowseCatalogBanner] = useState(true);

  const isFiltersOnLeft = useSelector(getIsFiltersOnLeft);

  const {
    storeBasePath,
    isPreviewCatalogMode,
    withMenu,
    countryCode,
    lang,
    storeCode,
    workspaceSlug,
    isNewRoute,
    isNewSwagmagicHost,
  } = useStoreContext();

  const { isSwagCreationShopFlow } = useCreateSwagContext();

  const isLockerShop = useSelector(getIsLockerShop);
  const isSinglePageShop = useSelector(getIsSinglePageShop);
  const catalogShopNumber = useConfig(getStadiumCatalogNumber);

  const showNewHeader = useShowNewHeader();

  const showLeftFilter =
    (isNewSwagmagicHost ? isDesktopDevice : !isMobileDevice) &&
    isFiltersOnLeft &&
    !isNewShopCreationFlow;

  const { parentCategoryCode, activeCategoryCode } = getParentCategoryFromCategory(categoryCode);

  const parentCategory = useMemo(() => {
    if (!isEmpty(menuCodeMapping))
      return getSelectedMenuItem(menuCodeMapping[parentCategoryCode], activeCategoryCode);
  }, [menuCodeMapping, activeCategoryCode]);

  const isMultiLevelMenu = useMemo(() => {
    return parentCategory?.children?.length > 0;
  }, [parentCategory]);

  const isNewShopFlowWithHeader = showNewHeader && isNewShopCreationFlow;
  const isDummyStoreMerch = !!parentCategory?.isDummyStoreMerch;

  const showLeftPanel =
    !isNewShopFlowWithHeader &&
    (isNewSwagmagicHost ? showLeftFilter : isMultiLevelMenu || showLeftFilter) &&
    !isDummyStoreMerch;

  const childCategory = useMemo(() => {
    if (parentCategory) return getSelectedMenuItem(parentCategory, subCategoryCode);
  }, [parentCategory, subCategoryCode]);

  const categoryName = parentCategory?.name || activeCategoryCode;
  const withSwag = parentCategory?.forSwag;

  const showCatalogBannerUnderMerch = withSwag && !isLockerShop && isPreviewCatalogMode;
  const showMenu =
    storeNumber && withMenu && catalogShopNumber !== storeNumber && !isSinglePageShop;

  const renderBackToHomeCta = () => {
    const homeRoute = getStoreBasePath({
      countryCode,
      lang,
      storeCode,
      storeNumber,
      isNewShopCreationFlow: isNewShopCreationFlow,
      isSwagCreationShopFlow: isSwagCreationShopFlow,
      workspaceSlug,
      isNewRoute,
    });
    return (
      <BackIconButton href={homeRoute} className={styles.backBtn}>
        Back
      </BackIconButton>
    );
  };

  const getBreadCrumbItems = () => {
    let items = [
      { name: "Home", link: getStoreHomeRoute({ storeBasePath }) },
      {
        name:
          subCategoryCode === ALL_SUBCATEGORY_CODE && categoryCode !== ALL_PRODUCTS
            ? `All ${categoryName}`
            : categoryName,
        link: getMenuItemRoute({
          category: categoryCode,
          storeBasePath,
        }),
      },
    ];

    if (subCategoryCode !== ALL_SUBCATEGORY_CODE) {
      items.push({
        name: childCategory?.name || subCategoryCode,
        link: getMenuItemRoute({
          storeBasePath,
          category: categoryCode,
        }),
      });
    }
    return items;
  };

  const shopAnalyticsContext = useShopAnalyticsContext();

  const renderSideBar = useCallback(
    (filterConfig) => {
      if (showLeftPanel)
        return (
          <div
            className={`${styles.colLeftSideBar} ${
              isNewSwagmagicHost ? styles.colLeftSideBarV2 : ""
            }`}
          >
            <div
              className={`${styles.leftSideBar} ${isNewSwagmagicHost ? styles.leftSideBarV2 : ""} ${
                showMenu ? "" : styles.withoutMenu
              } ${isSwagCreationShopFlow ? styles.withSwagSearchFlow : ""}`}
            >
              {isLockerShop && !isNewSwagmagicHost && (
                <div className={styles.shippingBannerCont}>
                  <ShippingBanner showAsBanner={false} />
                </div>
              )}
              {isNewSwagmagicHost ? (
                <FilterWrapper
                  config={filterConfig}
                  eventLocationAnalytics={{
                    track: !!shopAnalyticsContext.shop_number,
                    target: ANALYTICS_TARGET_FILTER,
                    eventProperties: shopAnalyticsContext,
                  }}
                />
              ) : (
                <Sidebar isMultiLevelMenu={isMultiLevelMenu} showLeftFilter={showLeftFilter} />
              )}
            </div>
          </div>
        );
    },
    [
      showLeftPanel,
      showMenu,
      isLockerShop,
      isNewSwagmagicHost,
      isMultiLevelMenu,
      showLeftFilter,
      isSwagCreationShopFlow,
      shopAnalyticsContext,
    ],
  );

  const renderNewShopBrowseCatalogBanner = () =>
    isNewShopCreationFlow &&
    showBrowseCatalogBanner && (
      <div className={styles.shopBrowseCatalogBanner}>
        <div>
          Browse our catalog below. Once you save your shop, you&apos;ll be able to enable/disable
          products and subcategories in your <strong>Shop Dashboard</strong>.
        </div>
        <InternalButton
          onClick={() => setShowBrowseCatalogBanner(false)}
          styleType="link"
          className={styles.iconClose}
        />
      </div>
    );

  const allProductCategory =
    subCategoryCode === ALL_SUBCATEGORY_CODE || parentCategoryCode !== DEFAULT_MENU_ITEM_CODE
      ? parentCategory
      : childCategory;

  const subCategoryMobile = Boolean(allProductCategory?.children?.length);

  const renderSubCategoryItem = ({ id, code, name }) => {
    return (
      <li className={styles.subCategoryListItem} key={id}>
        <Link
          key={id}
          prefetch={false}
          href={getMenuItemRoute({
            storeBasePath,
            category: code,
          })}
          className={`${styles.categoryItemLink} ${code === subCategoryCode ? styles.active : ""}`}
        >
          {name}
        </Link>
      </li>
    );
  };

  const renderSubCategories = () => {
    const subCategories = [
      {
        name: "All Products",
        code: parentCategoryCode === DEFAULT_MENU_ITEM_CODE ? subCategoryCode : activeCategoryCode,
        productCategoryCode:
          parentCategoryCode === DEFAULT_MENU_ITEM_CODE ? categoryCode : parentCategoryCode,
      },
      ...(allProductCategory?.children || []),
    ];

    const categoryName = allProductCategory?.name;

    if (
      !isMobileDevice &&
      ((childCategory?.depth === 2 && !childCategory?.children?.length) ||
        subCategoryCode === ALL_SUBCATEGORY_CODE)
    ) {
      return "";
    }

    return (
      <>
        {allProductCategory?.code !== ALL_PRODUCTS && (
          <h2 className={styles.pageListTitle}>{categoryName}</h2>
        )}
        {Boolean(allProductCategory?.children?.length) && (
          <ul className={styles.subCategoryListing}>{subCategories.map(renderSubCategoryItem)}</ul>
        )}
      </>
    );
  };

  const banner = (() => {
    if (isMobileDevice) {
      return childCategory?.mobileBanner || parentCategory?.mobileBanner;
    }
    return childCategory?.banner || parentCategory?.banner;
  })();

  const renderProductsListing = () => {
    if (parentCategory?.isDummyStoreMerch) return <DummyStoreMerchBanner />;
    return (
      <>
        {renderNewShopBrowseCatalogBanner()}
        {showNewHeader &&
          !isMobileDevice &&
          showMenu &&
          !isNewSwagmagicHost &&
          renderSubCategories()}
        <ProductListing
          slug={slug}
          showLeftFilter={showLeftFilter}
          showLeftPanel={showLeftPanel}
          topBannerPresent={banner}
          renderFilter={isNewSwagmagicHost ? renderSideBar : undefined}
        />
      </>
    );
  };

  const renderResponsiveCategoriesView = () =>
    isMobileDevice ? <MobileMenuCategories /> : <TabMenuParentCategories />;

  const renderProductsContainer = () => {
    const merchClassName = showCatalogBannerUnderMerch ? styles["categoryMerchBlock"] : "";
    const newFilterClassName = isNewSwagmagicHost ? styles["colRightSideBarV2"] : "";
    const className = showLeftPanel
      ? `${styles["colRightSideBar"]} ${newFilterClassName} ${merchClassName}`
      : styles["colWithoutLeftSidebar"];

    const topContent = childCategory?.topContent || parentCategory?.topContent;
    const showListingBanner =
      !showBannerOnProductListing &&
      !showLeftFilter &&
      !isNewShopCreationFlow &&
      !(topContent || banner);

    return (
      <article className={className}>
        <div className={styles.listWrapper}>
          {isPreviewCatalogMode && !isDesktopDevice ? (
            renderResponsiveCategoriesView()
          ) : (
            <>
              {isNewShopCreationFlow || isSwagCreationShopFlow ? (
                <div className={styles.backButtonBlock}>{renderBackToHomeCta()}</div>
              ) : (
                !showNewHeader &&
                !isNewSwagmagicHost && (
                  <BreadcrumbContainer items={getBreadCrumbItems()} className="store-breadcrumb" />
                )
              )}
              {showListingBanner && !showNewHeader && <ProductListingBanner />}
            </>
          )}
          {showCatalogBannerUnderMerch ? <SwagCatalogBanner /> : renderProductsListing()}
        </div>
      </article>
    );
  };

  const renderFooterContent = () => {
    const bottomContent = childCategory?.bottomContent || parentCategory?.bottomContent;
    return (
      bottomContent && (
        <div className={styles.footerBannerArea}>{ReactHtmlParser(bottomContent)}</div>
      )
    );
  };

  const renderProductHeader = () => {
    const topContent = childCategory?.topContent || parentCategory?.topContent;

    if (
      showBannerOnProductListing ||
      (!topContent && !banner) ||
      subCategoryCode !== ALL_SUBCATEGORY_CODE
    )
      return <></>;

    return (
      <div className={styles.fullWidthBannerSection}>
        {banner && (
          <Multimedia
            type="image"
            height={205}
            className={styles.categoryBannerImg}
            src={banner}
            alt="Category Banner"
            fetchPriority="high"
          />
        )}
        {topContent && (
          <div className={styles.categoryBannerText}>
            <div className={styles.bannerText}>{ReactHtmlParser(topContent)}</div>
          </div>
        )}
      </div>
    );
  };

  if (!storeNumber || menu?.loading)
    return <Loaders.PageLoader wrapperClass="pageLoaderwrap min-h-100" />;

  if (showCatalogBannerUnderMerch && isDesktopDevice) return <SwagCatalogBanner />;

  return (
    <section
      className={`${styles.fullwidthBg} ${styles.onlyListingPage}`}
      ref={LISTING_SECTION_REF}
      style={{ "--sticky-header-height": `${STICKY_HEADER_REF.current?.clientHeight}px` }}
    >
      <ListingScrollReset />
      {showMenu &&
        isMobileDevice &&
        showNewHeader &&
        !isPreviewCatalogMode &&
        !isNewSwagmagicHost &&
        parentCategory?.code !== ALL_PRODUCTS && (
          <>
            <div
              className={`${styles.responsiveSubCategorySection} ${
                subCategoryMobile ? "" : styles.thirdNavNotAvailable
              }`}
              style={{ "--sticky-header-height": `${STICKY_HEADER_REF.current?.clientHeight}px` }}
            >
              {renderSubCategories()}
            </div>
            <div className={styles.responsiveSubCategorySectionHolder} />
          </>
        )}

      <div className={styles.storePageContainer}>
        {renderProductHeader()}
        <div className={`${styles.rowWrapper} ${styles.rowWrapperMain}`}>
          <ProductListingProvider>
            {!isNewSwagmagicHost && !showCatalogBannerUnderMerch && renderSideBar()}
            {renderProductsContainer()}
            {renderFooterContent()}
          </ProductListingProvider>
        </div>
      </div>
    </section>
  );
};

export default StoreProductsListingContainer;
